import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DraftCardShell, { primaryButtonSx, textButtonSx } from "../drafts/DraftCardShell";
import { catalogueName } from "../drafts/quizTitleSelection";
import { AI_ASSISTANT_BLUE } from "../constants";

const CATALOGUES = ["histology", "embryology", "anatomy"];

/**
 * Pick which titles a quiz draws from: Microlab bank titles for a subject, or
 * the professor's own private titles. Nothing is created here — the choice
 * fills the quiz draft, which still needs "Create quiz".
 */
export default function QuizTitlePickerCard({ picker, onLoad, onApply, onClose, disabled }) {
  const [source, setSource] = useState(picker.source === "private" ? "private" : "microlab");
  const [catalogue, setCatalogue] = useState(picker.catalogue || "histology");
  const [search, setSearch] = useState(picker.search || "");
  const [state, setState] = useState({ loading: true, error: "", titles: [] });
  const [selected, setSelected] = useState(() => new Map());
  const [allSubjects, setAllSubjects] = useState(false);
  const [numQuestions, setNumQuestions] = useState("10");
  const [timeLimit, setTimeLimit] = useState("20");
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setSource(picker.source === "private" ? "private" : "microlab");
    if (picker.catalogue) setCatalogue(picker.catalogue);
    setSearch(picker.search || "");
  }, [picker.source, picker.catalogue, picker.search]);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", titles: [] });
    setSelected(new Map());
    onLoad({ source, catalogue: source === "microlab" ? catalogue : null })
      .then((res) => {
        if (!cancelled) setState({ loading: false, error: "", titles: res?.titles || [] });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || "Couldn't load titles.", titles: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [source, catalogue, onLoad]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = state.titles;
    // Private titles default to the chosen subject; "All subjects" shows the rest.
    if (source === "private" && catalogue && !allSubjects) {
      const inSubject = list.filter((t) => t.catalogue === catalogue);
      if (inSubject.length) list = inSubject;
    }
    if (q) {
      const words = q.split(/\s+/).filter(Boolean);
      const matches = list.filter((t) => {
        const hay = `${t.title} ${t.group || ""}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      });
      // A free-text topic may not match any title name; fall back to all.
      if (matches.length) list = matches;
    }
    return list;
  }, [state.titles, search, source, catalogue, allSubjects]);

  const selectedList = [...selected.values()];
  const selectedQuestions = selectedList.reduce((sum, t) => sum + (t.question_count || 0), 0);
  const maxQuestions = source === "private" ? 200 : 50;
  const count = Math.max(1, Math.min(maxQuestions, Math.round(Number(numQuestions)) || 0));
  const tooFewForTitles = source === "private" && count < selectedList.length;

  const toggle = (title) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(title.id)) next.delete(title.id);
      else next.set(title.id, title);
      return next;
    });
  };

  const allVisibleSelected = visible.length > 0 && visible.every((t) => selected.has(t.id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allVisibleSelected) visible.forEach((t) => next.delete(t.id));
      else visible.forEach((t) => next.set(t.id, t));
      return next;
    });
  };

  const apply = (wholeCatalogue = false) =>
    onApply({
      source,
      catalogue: source === "microlab" ? catalogue : null,
      titles: wholeCatalogue ? [] : selectedList,
      wholeCatalogue,
      numQuestions: count,
      timeLimit: Math.max(1, Math.min(300, Math.round(Number(timeLimit)) || 20)),
    });

  let lastGroup = null;

  return (
    <DraftCardShell
      kicker="CHOOSE QUIZ TITLES"
      title={source === "private" ? "My titles" : `${catalogueName(catalogue)} · Microlab bank`}
      summary={
        selectedList.length
          ? `${selectedList.length} selected · ${selectedQuestions} question${selectedQuestions === 1 ? "" : "s"} available`
          : "Select one or more titles"
      }
      icon={<ChecklistRoundedIcon fontSize="small" />}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      headerAction={
        <IconButton size="small" aria-label="Close title picker" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      }
      footer={
        <Stack spacing={0.9}>
          <Stack direction="row" spacing={0.75}>
            <TextField
              size="small"
              type="number"
              label="Questions"
              value={numQuestions}
              disabled={disabled}
              onChange={(e) => setNumQuestions(e.target.value)}
              inputProps={{ min: 1, max: maxQuestions }}
              sx={{ width: 110 }}
              helperText={
                tooFewForTitles
                  ? `At least ${selectedList.length}`
                  : selectedList.length && count > selectedQuestions
                    ? `Only ${selectedQuestions} available`
                    : " "
              }
              error={tooFewForTitles}
            />
            <TextField
              size="small"
              type="number"
              label="Minutes"
              value={timeLimit}
              disabled={disabled}
              onChange={(e) => setTimeLimit(e.target.value)}
              inputProps={{ min: 1, max: 300 }}
              sx={{ width: 100 }}
              helperText=" "
            />
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={disabled || !selectedList.length || tooFewForTitles}
              onClick={() => apply(false)}
              sx={primaryButtonSx}
            >
              Use {selectedList.length || ""} title{selectedList.length === 1 ? "" : "s"}
            </Button>
            {source === "microlab" ? (
              <Button size="small" disabled={disabled || !state.titles.length} onClick={() => apply(true)} sx={textButtonSx}>
                Use all {catalogueName(catalogue)} titles
              </Button>
            ) : null}
          </Stack>
        </Stack>
      }
    >
      <Tabs
        value={source}
        onChange={(_, value) => setSource(value)}
        variant="fullWidth"
        sx={{ minHeight: 34, mb: 1, "& .MuiTab-root": { minHeight: 34, textTransform: "none", fontWeight: 700, fontSize: 12.5 } }}
      >
        <Tab value="microlab" label="Microlab bank" />
        <Tab value="private" label="My titles" />
      </Tabs>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={catalogue}
        onChange={(_, value) => value && setCatalogue(value)}
        sx={{ mb: 1, display: "flex", "& .MuiToggleButton-root": { flex: 1, textTransform: "none", fontSize: 12, py: 0.4 } }}
        aria-label="Subject"
      >
        {CATALOGUES.map((c) => (
          <ToggleButton key={c} value={c}>
            {catalogueName(c)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        size="small"
        fullWidth
        placeholder="Search titles"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        inputProps={{ "aria-label": "Search titles" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" alignItems="center" sx={{ mt: 0.5 }}>
        <Button size="small" disabled={!visible.length} onClick={toggleAllVisible} sx={{ ...textButtonSx, fontSize: 12 }}>
          {allVisibleSelected ? "Clear these" : `Select all ${visible.length}`}
        </Button>
        <Box sx={{ flex: 1 }} />
        {source === "private" ? (
          <Button size="small" onClick={() => setAllSubjects((v) => !v)} sx={{ ...textButtonSx, fontSize: 12, color: "#64748b" }}>
            {allSubjects ? `Only ${catalogueName(catalogue)}` : "All subjects"}
          </Button>
        ) : null}
      </Stack>

      <Box role="list" sx={{ maxHeight: 280, overflowY: "auto", mt: 0.25, pr: 0.25 }}>
        {state.loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
            <CircularProgress size={20} sx={{ color: AI_ASSISTANT_BLUE }} />
          </Box>
        ) : null}
        {state.error ? <Typography sx={{ fontSize: 12.5, color: "#b91c1c", py: 1 }}>{state.error}</Typography> : null}
        {!state.loading && !state.error && !visible.length ? (
          <Typography sx={{ fontSize: 12.5, color: "#64748b", py: 2, textAlign: "center" }}>
            {source === "private"
              ? "You don’t have private titles with questions yet. Add them in Course Catalog / Question Bank, or use the Microlab bank."
              : `No ${catalogueName(catalogue)} titles with questions yet.`}
          </Typography>
        ) : null}
        {visible.map((t) => {
          const showGroup = t.group && t.group !== lastGroup;
          lastGroup = t.group;
          const empty = !t.question_count;
          return (
            <Box key={t.id}>
              {showGroup ? (
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", letterSpacing: 0.4, mt: 1, mb: 0.25, textTransform: "uppercase" }}>
                  {t.group}
                </Typography>
              ) : null}
              <Box
                role="listitem"
                onClick={() => !empty && toggle(t)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: 1.5,
                  cursor: empty ? "default" : "pointer",
                  opacity: empty ? 0.5 : 1,
                  "&:hover": empty ? undefined : { bgcolor: "#f1f5f9" },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected.has(t.id)}
                  disabled={empty}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(t)}
                  inputProps={{ "aria-label": t.title }}
                />
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "#0f172a" }} noWrap title={t.title}>
                  {t.title}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b", pr: 1, flexShrink: 0 }}>
                  {t.question_count} q
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DraftCardShell>
  );
}

QuizTitlePickerCard.propTypes = {
  picker: PropTypes.shape({
    source: PropTypes.oneOf(["microlab", "private"]),
    catalogue: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  onLoad: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
